import React, { useEffect, useState } from 'react';
import axios from 'axios';

import SyntaxHighlighter from 'react-syntax-highlighter';
import { foundation } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import { Typography } from '@material-ui/core';

import useStyle from './style';

import Loading from 'components/loading';

interface Props {
  path: string;
  name: string;
}

const JsonViewer: React.FC<Props> = ({ path, name }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jsonText, setJsonText] = useState<string | null>(null);
  const classes = useStyle();

  useEffect(() => {
    if (!path) return;
    let cancelled = false;

    async function fetchJson(): Promise<void> {
      try {
        setLoading(true);
        setError(null);

        const resBuffer = await axios.get(path, { responseType: 'arraybuffer', external: true });
        if (cancelled) return;
        const decoder = new TextDecoder('utf-8');
        const text = decoder.decode(resBuffer as any as ArrayBuffer);
        try {
          const parsed = JSON.parse(text);
          setJsonText(JSON.stringify(parsed, null, 2));
        } catch (e) {
          // not valid JSON — show raw text
          setJsonText(text);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load JSON');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchJson();

    return () => {
      cancelled = true;
    };
  }, [path]);

  if (loading) return <Loading loading testId="json-viewer-loader" />;
  if (error) return <div className={classes.mainContainer}>{error}</div>;
  if (!jsonText) return <div className={classes.mainContainer}>No JSON to display</div>;

  return (
    <div className={classes.mainContainer} data-testid="json-viewer">
      <Typography className={classes.excelTitle} variant="button">
        {name}
      </Typography>
      <SyntaxHighlighter
        language={'json'}
        className={classes.code}
        data-testid={`json-file`}
        style={foundation}
        wrapLines
        wrapLongLines
      >
        {jsonText}
      </SyntaxHighlighter>
    </div>
  );
};

export default JsonViewer;
